import React from 'react';

import cn from 'clsx';

import { Box, Text } from '@r-client/shared/ui/core';
import { Location, SvgIconLight } from '@r-client/shared/ui/icons';
import { ClientRender } from '@r-client/shared/util/components';

import { TGetEventsEvent } from '../../../types';
import { getEventHours, humanizeCategory } from '../../../utils';
import { EventCategoryIcon } from '../../event-category-icon/event-category-icon';
import { EventProperty } from '../../event-property/event-property';
import { WeekDay } from '../../week-day/week-day';

import styles from './event-card-header.module.scss';

interface IEventCardHeaderProps {
  event: TGetEventsEvent;
  testIds?: {
    eventHours?: string;
  };
}

export const EventCardHeader: React.FC<
  React.PropsWithChildren<IEventCardHeaderProps>
> = ({ event, testIds, children }) => {
  return (
    <header className={styles.header}>
      <Box m="0 0 0.5brm 0">
        {!!event.category && (
          <EventProperty
            className={cn(styles.detail, styles.detailPrimary, styles.category)}
          >
            <EventCategoryIcon
              category={event.category}
              className={styles.categoryIconWrapper}
              iconClassName={styles.secondaryIcon}
            />
            <Text as="span" weight="300" m="0 2px 0 0">
              {humanizeCategory(event.category)}
            </Text>
          </EventProperty>
        )}

        {!!event.address?.city && (
          <EventProperty className={cn(styles.detail, styles.detailPrimary)}>
            <SvgIconLight
              icon={Location}
              className={styles.locationIcon}
              iconClassName={styles.secondaryIcon}
            />
            <Text as="span" weight="300" m="0 2px 0 0">
              {event.address.city}
            </Text>
          </EventProperty>
        )}

        {!!event.live && (
          <EventProperty className={cn(styles.detail, styles.detailDanger)}>
            <Text as="span" weight="300" m="0 2px 0 0">
              Live now
            </Text>
          </EventProperty>
        )}

        <ClientRender>
          {!!event.future && (
            <EventProperty className={cn(styles.detail, styles.detailPrimary)}>
              <WeekDay date={event.beginAt} today="Today" tomorrow="Tomorrow" />
            </EventProperty>
          )}

          {!!event.beginAt && (
            <EventProperty
              className={styles.detail}
              dataTestId={testIds?.eventHours}
            >
              <Text
                as="span"
                className={cn(styles.eventTimeFull, {
                  [styles.futureEventTime]: event.future,
                })}
              >
                {getEventHours(event.timezone, event.beginAt, event.endAt)}
              </Text>
              <Text
                as="span"
                className={cn(styles.eventTimeShort, {
                  [styles.futureEventTime]: event.future,
                })}
              >
                {getEventHours(event.timezone, event.endAt)}
              </Text>
            </EventProperty>
          )}
        </ClientRender>
      </Box>

      {children}
    </header>
  );
};
